/**
 * The offline write queue.
 *
 * A teacher taking the register in a courtyard, a cashier at a desk on a
 * saturated 3G link: the network goes and the work must not. A write attempted
 * offline is queued and drains when the connection returns.
 *
 * Three properties carry the whole design, and each exists because of a way
 * this goes wrong:
 *
 *  1. EVERY ROW HAS AN IDEMPOTENCY KEY, minted once at enqueue. The dangerous
 *     failure is not "the request failed" — it is "the server applied it and
 *     the acknowledgement was lost". Replaying with the same key makes the
 *     server dedupe instead of double-marking a child or double-charging a
 *     family.
 *
 *  2. A DEFINITIVE REJECTION STOPS. A hard 4xx means the server has decided;
 *     re-sending it every drain hammers a server that already said no, and
 *     the row never surfaces to a human. Those rows go to `failed` and wait
 *     for someone to retry or discard them. A retryable error that exhausts
 *     MAX_ATTEMPTS lands there too, so nothing retries forever invisibly.
 *
 *  3. CRASH RECOVERY. A row left `in_flight` by a killed tab is reset to
 *     `pending` at boot. Safe precisely because of (1).
 *
 * The drain is single-flight: two triggers (regained connectivity and the
 * periodic timer) must not replay the same rows twice.
 */
import { ApiError, request } from "@/api/client";
import { openDb, scopeFields, type OutboxRow } from "./db";

/** After this many tries a row stops retrying and waits for a human. */
const MAX_ATTEMPTS = 8;

/**
 * How long one queued request may take before we give up on it.
 *
 * NOT a nicety — without it the queue can wedge permanently. A fetch issued
 * while the link is black-holed (a captive portal, or a connection that went
 * away mid-flight) never settles: the row stays `in_flight`, `draining` stays
 * true, and EVERY later drain returns immediately. Observed exactly that —
 * one row stuck at `in_flight`, attempts: 0, no error, and a queue that never
 * moved again for the rest of the session.
 *
 * Aborting turns the hang into an ordinary network failure, which the retry
 * classification already knows how to handle.
 */
const REQUEST_TIMEOUT_MS = 10_000;

export type OutboxEvent =
  | { type: "queued"; entity: string }
  | { type: "acked"; entity: string; response: unknown }
  | { type: "failed"; entity: string; error: string };

type Listener = (event: OutboxEvent) => void;
const listeners = new Set<Listener>();

export function onOutbox(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit(event: OutboxEvent) {
  for (const listener of listeners) {
    try { listener(event); } catch { /* a bad listener must not stop the drain */ }
  }
}

/**
 * A sentence a cashier can act on, instead of the browser's own words.
 *
 * `last_error` is rendered in the failed-row review, so "signal is aborted
 * without reason" — which is literally what an AbortController produces — would
 * end up in front of someone holding cash. The server's French `detail` is kept
 * as-is when there is one: it is written for this reader.
 */
function humanError(cause: unknown, status: number): string {
  const raw = cause instanceof Error ? cause.message : String(cause);
  if (/aborted/i.test(raw)) return "Délai dépassé — le serveur n'a pas répondu.";
  if (status === 0) return "Pas de réseau au moment de l'envoi.";
  if (status >= 500) return `Erreur du serveur (${status}).`;
  return raw;
}

/**
 * Is this worth trying again?
 *
 * 0 is a network failure. 401 means re-authenticate (the silent refresh may
 * fix it). 408/429 are explicitly transient. 5xx is the server's problem.
 * Everything else — 400, 403, 404, 409, 422 — is the server having decided,
 * and retrying is just noise.
 */
function isRetryable(status: number): boolean {
  if (status === 0) return true;
  if (status === 401 || status === 408 || status === 429) return true;
  return status >= 500;
}

/**
 * Queue a write.
 *
 * Returns the row's `seq`, or null when offline storage is unavailable — the
 * caller then behaves exactly as it did before, online-only.
 */
export async function enqueue(item: {
  entity: string;
  /**
   * ORG-RELATIVE path — everything after `/orgs/{id}/`.
   *
   * The row carries its own org_id and the drain rebuilds the URL from THAT,
   * not from whatever org is current when it finally sends. A mark taken in
   * one school must never replay into another because the user switched
   * accounts in between.
   */
  path: string;
  method?: string;
  payload: unknown;
  meta?: Record<string, unknown> | null;
}): Promise<number | null> {
  const db = openDb();
  if (!db) return null;
  const now = Date.now();
  const row: OutboxRow = {
    ...scopeFields(),
    entity: item.entity,
    path: item.path,
    method: item.method ?? "POST",
    payload: item.payload,
    idempotency_key: crypto.randomUUID(),
    status: "pending",
    attempts: 0,
    created_at: now,
    last_attempt_at: 0,
    meta: item.meta ?? null,
  };
  try {
    const seq = await db.outbox.add(row);
    emit({ type: "queued", entity: row.entity });
    return seq;
  } catch {
    return null;
  }
}

/** The URL a row replays to, built from the row's OWN org. */
function endpointFor(row: OutboxRow): string {
  return `/api/v1/orgs/${row.org_id}/${row.path}`;
}

let draining = false;

/**
 * Send every pending row, oldest first. Single-flight.
 *
 * `failed` rows are deliberately NOT included: they are waiting for a human.
 */
export async function drainOutbox(
  { timeoutMs = REQUEST_TIMEOUT_MS }: { timeoutMs?: number } = {},
): Promise<{ acked: number; failed: number }> {
  if (draining) return { acked: 0, failed: 0 };
  const db = openDb();
  if (!db) return { acked: 0, failed: 0 };

  draining = true;
  let acked = 0;
  let failed = 0;
  try {
    const rows = await db.outbox.where("status").equals("pending").sortBy("seq");
    for (const row of rows) {
      if (row.seq === undefined) continue;
      await db.outbox.update(row.seq, {
        status: "in_flight", last_attempt_at: Date.now(),
      });
      const abort = new AbortController();
      const timeout = window.setTimeout(() => abort.abort(), timeoutMs);
      try {
        const response = await request<unknown>(endpointFor(row), {
          method: row.method,
          body: row.payload,
          idempotencyKey: row.idempotency_key,
          signal: abort.signal,
        });
        // Acked: the server holds the data now, so the row is done.
        await db.outbox.delete(row.seq);
        acked += 1;
        emit({ type: "acked", entity: row.entity, response });
      } catch (cause) {
        const status = cause instanceof ApiError ? cause.status : 0;
        const attempts = row.attempts + 1;
        const retryable = isRetryable(status) && attempts < MAX_ATTEMPTS;
        const message = humanError(cause, status);
        await db.outbox.update(row.seq, {
          status: retryable ? "pending" : "failed",
          attempts,
          last_error: isRetryable(status) && !retryable
            ? `max_attempts: ${message}`
            : message,
        });
        if (!retryable) {
          failed += 1;
          emit({ type: "failed", entity: row.entity, error: message });
        }
        // Stop the pass on a connectivity failure: the rest will fail the same
        // way, and burning their attempt budget on a known-dead link is how a
        // queue ends up "failed" for no reason but a tunnel.
        if (status === 0) break;
      } finally {
        window.clearTimeout(timeout);
      }
    }
  } catch {
    // Storage went away mid-drain. The rows are still there next time.
  } finally {
    draining = false;
    // Anything still marked in_flight when the pass ends was interrupted, not
    // sent. Recovering here (not only at boot) means a wedged row heals on the
    // next drain instead of sitting untouched for the rest of the session.
    await recoverInFlight();
  }
  return { acked, failed };
}

/** Reset rows a killed tab left in flight. Safe because of the idempotency key. */
export async function recoverInFlight(): Promise<void> {
  const db = openDb();
  if (!db) return;
  try {
    const stuck = await db.outbox.where("status").equals("in_flight").toArray();
    for (const row of stuck) {
      if (row.seq !== undefined) await db.outbox.update(row.seq, { status: "pending" });
    }
  } catch { /* nothing to recover */ }
}

/** `{ pending, failed }` for the current scope, optionally for one entity. */
export async function outboxCounts(entity?: string): Promise<{ pending: number; failed: number }> {
  const db = openDb();
  if (!db) return { pending: 0, failed: 0 };
  try {
    const all = await db.outbox.toArray();
    const mine = entity ? all.filter((row) => row.entity === entity) : all;
    return {
      pending: mine.filter((row) => row.status !== "failed").length,
      // Counted separately and surfaced on purpose: a rejected write that
      // vanishes from every indicator is a write the school believes it made.
      failed: mine.filter((row) => row.status === "failed").length,
    };
  } catch {
    return { pending: 0, failed: 0 };
  }
}
